import React, { useState, useEffect } from 'react';
import {
  Shield,
  Search,
  Bell,
  RefreshCw,
  Clock,
  MapPin,
  ExternalLink,
  ChevronRight,
  ShieldAlert,
  Sliders,
  LogOut,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

interface AdminHeaderProps {
  activeTab: string;
  onRefresh: () => void;
  isRefreshing: boolean;
  onOpenSearch: () => void;
  pendingVerificationsCount: number;
  openDisputesCount: number;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  activeTab,
  onRefresh,
  isRefreshing,
  onOpenSearch,
  pendingVerificationsCount,
  openDisputesCount,
}) => {
  const { currentUser, navigateTo, adminLogout } = useApp();
  const [watTime, setWatTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      // Format to West Africa Time (UTC+1)
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'Africa/Lagos',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      };
      setWatTime(new Intl.DateTimeFormat('en-GB', options).format(now));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const tabLabels: Record<string, string> = {
    overview: 'Executive Dashboard',
    professionals: 'Artisans & Professionals',
    customers: 'Customer Registry',
    jobs: 'Job Matching & Pipeline',
    locations: 'Nigerian States & LGAs Coverage (774 LGAs)',
    verifications: 'ID & Trade Verification Centre',
    subscriptions: 'Marketplace Financials & Commissions',
    notifications: 'System Notifications & Email/SMS Alerts',
    whatsapp: 'WhatsApp AI Control Centre',
    telegram: 'Telegram AI Assistant Operations',
    ai_monitoring: 'Gemini AI Operational Health',
    reviews: 'Ratings & Reviews Moderation',
    disputes: 'Dispute & Resolution Desk',
    broadcast: 'System Broadcast & Alerts',
    audit_logs: 'Immutable Audit Log Ledger',
  };

  return (
    <div id="admin-header" className="bg-slate-900 border-b border-slate-800 text-white shadow-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Top Operational Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-black tracking-tight text-white flex items-center gap-2">
                  JobMatch NG
                  <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase bg-emerald-500 text-slate-950 tracking-wider">
                    Admin Control Centre
                  </span>
                </h1>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-2">
                <span>Enterprise Management Console</span>
                <span className="text-slate-600">•</span>
                <span className="text-emerald-400 font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Production Live
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-400 flex items-center gap-1 font-mono text-[11px]">
                  <Clock className="w-3 h-3 text-slate-500" />
                  {watTime || 'WAT (Lagos)'}
                </span>
              </p>
            </div>
          </div>

          {/* Quick Action Controls */}
          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Global Search Trigger */}
            <button
              id="admin-search-trigger-btn"
              onClick={onOpenSearch}
              className="flex items-center gap-2 bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white px-3.5 py-2 rounded-xl text-xs font-medium border border-slate-700/60 transition-all shadow-sm"
              title="Global search across all platform entities"
            >
              <Search className="w-3.5 h-3.5 text-emerald-400" />
              <span>Universal Search...</span>
              <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-slate-700 rounded text-slate-400 border border-slate-600">
                /
              </kbd>
            </button>

            {/* Verification Alert Badge */}
            {pendingVerificationsCount > 0 && (
              <button
                onClick={() => {}}
                className="flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 px-3 py-2 rounded-xl text-xs font-semibold transition-colors"
                title={`${pendingVerificationsCount} pending artisan verifications`}
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>{pendingVerificationsCount} Pending ID</span>
              </button>
            )}

            {/* Disputes Alert Badge */}
            {openDisputesCount > 0 && (
              <button
                onClick={() => {}}
                className="flex items-center gap-1.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 px-3 py-2 rounded-xl text-xs font-semibold transition-colors"
                title={`${openDisputesCount} active disputes requiring attention`}
              >
                <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping"></span>
                <span>{openDisputesCount} Dispute{openDisputesCount > 1 ? 's' : ''}</span>
              </button>
            )}

            {/* Refresh Button */}
            <button
              id="admin-refresh-btn"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-2 bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-700/60 transition-colors disabled:opacity-50"
              title="Refresh all real-time platform data"
            >
              <RefreshCw className={`w-4 h-4 text-slate-300 ${isRefreshing ? 'animate-spin text-emerald-400' : ''}`} />
            </button>

            {/* Back to Public Site */}
            <button
              id="admin-exit-view-btn"
              onClick={() => navigateTo('home')}
              className="flex items-center gap-1 bg-slate-800/60 hover:bg-slate-800 text-slate-300 hover:text-white px-3 py-2 rounded-xl text-xs font-medium border border-slate-700/40 transition-colors cursor-pointer"
            >
              <span>User View</span>
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </button>

            {/* Secure Admin Logout */}
            <button
              id="admin-logout-btn"
              onClick={adminLogout}
              className="flex items-center gap-1.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 hover:text-rose-200 border border-rose-500/40 px-3 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
              title="Terminate Admin Session & Sign Out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Breadcrumb & Section Subheader */}
        <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">Admin Control</span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-emerald-400 font-semibold">{tabLabels[activeTab] || 'Section'}</span>
          </div>

          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <span>Logged in as: <strong className="text-slate-200">{currentUser?.fullName || 'Administrator'}</strong></span>
            <span className="text-slate-400">•</span>
            <span>Role: <span className="text-emerald-400 uppercase font-bold">Administrator</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};
